import { useCallback } from 'react';
import { ModalsKeys } from 'global';
import { logError } from 'utils/logger';
import { useModal } from 'hooks/useModal';
import { useMutation } from '@apollo/client';
import { getOperationName } from 'utils/apollo';
import { useNotifications } from 'modules/notification';
import {
  CREATE_EQUIPMENT,
  DELETE_EQUIPMENT,
  GET_LIST_OF_EQUIPMENT_BY_VO_ID,
  UPDATE_EQUIPMENT,
  CreateEquipmentVariables,
  CreateUpdateEquipmentResponse,
  DeleteEquipmentVariables,
  UpdateEquipmentVariables,
} from '../api';

export function useEquipmentActions() {
  const { showNotification } = useNotifications();
  const { handleOpen } = useModal(ModalsKeys.Confirmation);

  const [createEquipment, { loading: isEquipmentCreating }] = useMutation<
    CreateUpdateEquipmentResponse,
    CreateEquipmentVariables
  >(CREATE_EQUIPMENT, {
    refetchQueries: [getOperationName(GET_LIST_OF_EQUIPMENT_BY_VO_ID)],
    onCompleted: () => {
      showNotification('Equipment created successfully.');
    },
    onError: err => {
      showNotification('Something went wrong ', { type: 'error' });
      logError(err);
    },
  });

  const [updateEquipment, { loading: isEquipmentUpdating }] = useMutation<
    CreateUpdateEquipmentResponse,
    UpdateEquipmentVariables
  >(UPDATE_EQUIPMENT, {
    refetchQueries: [getOperationName(GET_LIST_OF_EQUIPMENT_BY_VO_ID)],
    onError: err => {
      showNotification('Something went wrong ', { type: 'error' });
      logError(err);
    },
  });

  const [deleteEquipment, { loading: isEquipmentDeleting }] = useMutation<void, DeleteEquipmentVariables>(
    DELETE_EQUIPMENT,
    {
      refetchQueries: [getOperationName(GET_LIST_OF_EQUIPMENT_BY_VO_ID)],
      onCompleted: () => {
        showNotification('Equipment deleted successfully.');
      },
      onError: err => {
        showNotification('Something went wrong ', { type: 'error' });
        logError(err);
      },
    }
  );

  const onCreateEquipment = useCallback(
    async (variables: CreateEquipmentVariables['input']) => {
      const { data } = await createEquipment({ variables: { input: variables } });

      if (!data?.upsertEquipment?.id) throw new Error('Equipment not created');

      return data.upsertEquipment.id;
    },
    [createEquipment]
  );

  const onUpdateEquipment = useCallback(
    async (
      variables: UpdateEquipmentVariables['input'],
      options: { withNotification: boolean } = { withNotification: true }
    ) => {
      const { data } = await updateEquipment({ variables: { input: variables } });

      if (!data?.upsertEquipment?.id) throw new Error('Equipment not updated');

      if (options.withNotification) showNotification('Equipment updated successfully.');

      return data.upsertEquipment.id;
    },
    [updateEquipment]
  );

  const onDeleteEquipment = useCallback(
    (variables: DeleteEquipmentVariables, action?: VoidFunction) => {
      handleOpen({
        onConfirm: async () => {
          await deleteEquipment({ variables });
          action?.();
        },
        title: 'Confirmation',
        message: 'Are you sure you want to delete Equipment?',
      });
    },
    [deleteEquipment, handleOpen]
  );

  return {
    onCreateEquipment,
    isEquipmentCreating,
    onUpdateEquipment,
    isEquipmentUpdating,
    onDeleteEquipment,
    isEquipmentDeleting,
    isAnyActionInProgress: isEquipmentDeleting || isEquipmentUpdating || isEquipmentCreating,
  };
}
